import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { FOUNDATION, ASSESSMENT_URL, COURSES_URL } from '../config/foundation';

/** "Spark node" mark — a gold spark at the centre of a small connected
    constellation (community + the TechLifeSpark/digital-divide theme).
    Tuned for the dark green nav/footer. */
function SparkMark({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <g stroke="#95D5B2" strokeWidth="2.5" strokeLinecap="round" opacity="0.7">
        <line x1="32" y1="32" x2="14" y2="18" />
        <line x1="32" y1="32" x2="50" y2="16" />
        <line x1="32" y1="32" x2="16" y2="48" />
        <line x1="32" y1="32" x2="49" y2="47" />
      </g>
      <circle cx="14" cy="18" r="4" fill="#95D5B2" />
      <circle cx="50" cy="16" r="4" fill="#95D5B2" />
      <circle cx="16" cy="48" r="4" fill="#95D5B2" />
      <circle cx="49" cy="47" r="4" fill="#95D5B2" />
      <path d="M32 17C33.7 27.5 36.5 30.3 47 32C36.5 33.7 33.7 36.5 32 47C30.3 36.5 27.5 33.7 17 32C27.5 30.3 30.3 27.5 32 17Z" fill="#E8C547" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5m0-5L10 14M9 5H5v14h14v-4" />
    </svg>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-mustard-cream flex flex-col">
      {/* Nav — frosted glass over the page */}
      <nav
        className={`sticky top-0 z-50 text-white bg-mustard-green-dark/85 backdrop-blur-lg border-b transition-all duration-300 ${
          scrolled ? 'border-white/10 shadow-lg shadow-black/20' : 'border-transparent'
        }`}
      >
        <div className="section">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="group flex items-center gap-2 flex-shrink-0">
              <SparkMark className="w-6 h-6 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" />
              <span className="font-sans text-base sm:text-lg font-extrabold tracking-tight leading-none">
                The LifeSpark<span className="text-mustard-gold font-semibold"> Foundation</span>
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              <NavLink to="/about" active={isActive('/about')}>About</NavLink>
              <NavLink to="/what-we-do" active={isActive('/what-we-do')}>What We Do</NavLink>
              <NavLink to="/programmes" active={isActive('/programmes')}>LifeSpark360</NavLink>
              <a
                href={COURSES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-underline inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Courses <ExternalIcon />
              </a>
              <NavLink to="/get-involved" active={isActive('/get-involved')}>Get Involved</NavLink>
              <NavLink to="/apply" active={isActive('/apply')}>Apply</NavLink>

              {isAuthenticated ? (
                <>
                  <div className="w-px h-5 bg-white/20 mx-2" />
                  <NavLink to="/dashboard" active={isActive('/dashboard')}>Dashboard</NavLink>
                  {user?.role === 'trainer' && <NavLink to="/trainer" active={isActive('/trainer')}>Clients</NavLink>}
                  {user?.role === 'admin' && <NavLink to="/admin" active={isActive('/admin')}>Admin</NavLink>}
                  <NavLink to="/community" active={isActive('/community')}>Community</NavLink>
                  <div className="w-px h-5 bg-white/20 mx-2" />
                  <Link to="/settings" className="text-white/70 hover:text-white text-xs font-medium px-2 py-1 transition-colors">
                    {user?.name?.split(' ')[0]}
                  </Link>
                  <button onClick={logout} className="text-white/50 hover:text-white text-xs px-2 py-1 transition-colors">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <div className="w-px h-5 bg-white/20 mx-2" />
                  <Link to="/login" className="text-white/80 hover:text-white text-sm font-medium px-3 py-1.5 transition-colors">
                    Sign In
                  </Link>
                  <Link to="/get-involved" className="btn-gold !py-2 !px-4 !text-xs ml-1">
                    Support Us
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 -mr-2" aria-label="Menu">
              <div className="w-5 flex flex-col gap-1">
                <span className={`block h-0.5 bg-white transition-transform ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
                <span className={`block h-0.5 bg-white transition-opacity ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 bg-white transition-transform ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 pb-4 bg-mustard-green-dark/95 backdrop-blur-lg">
            <div className="section pt-3 space-y-1">
              <MobileLink to="/about" onClick={() => setMobileOpen(false)}>About</MobileLink>
              <MobileLink to="/what-we-do" onClick={() => setMobileOpen(false)}>What We Do</MobileLink>
              <MobileLink to="/programmes" onClick={() => setMobileOpen(false)}>LifeSpark360</MobileLink>
              <a
                href={COURSES_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-white/90 hover:text-white text-sm font-medium rounded-md hover:bg-white/5 transition-colors"
              >
                Courses <ExternalIcon />
              </a>
              <MobileLink to="/get-involved" onClick={() => setMobileOpen(false)}>Get Involved</MobileLink>
              <MobileLink to="/apply" onClick={() => setMobileOpen(false)}>Apply</MobileLink>
              {isAuthenticated ? (
                <>
                  <div className="h-px bg-white/10 my-2" />
                  <MobileLink to="/dashboard" onClick={() => setMobileOpen(false)}>Dashboard</MobileLink>
                  <MobileLink to="/community" onClick={() => setMobileOpen(false)}>Community</MobileLink>
                  <MobileLink to="/settings" onClick={() => setMobileOpen(false)}>Settings</MobileLink>
                  {user?.role === 'trainer' && <MobileLink to="/trainer" onClick={() => setMobileOpen(false)}>Clients</MobileLink>}
                  {user?.role === 'admin' && <MobileLink to="/admin" onClick={() => setMobileOpen(false)}>Admin</MobileLink>}
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="w-full text-left px-3 py-2 text-white/50 text-sm">Sign Out</button>
                </>
              ) : (
                <>
                  <div className="h-px bg-white/10 my-2" />
                  <MobileLink to="/login" onClick={() => setMobileOpen(false)}>Sign In</MobileLink>
                  <MobileLink to="/get-involved" onClick={() => setMobileOpen(false)}>Support Us</MobileLink>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="relative bg-mustard-green-dark text-white/60">
        {/* Gold accent hairline */}
        <div className="h-px bg-gradient-to-r from-transparent via-mustard-gold/50 to-transparent" />
        <div className="section py-14 lg:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10">
            <div className="sm:col-span-2">
              <Link to="/" className="group flex items-center gap-2 mb-3">
                <SparkMark className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
                <span className="font-sans text-lg font-extrabold text-white tracking-tight">
                  The LifeSpark<span className="text-mustard-gold font-semibold"> Foundation</span>
                </span>
              </Link>
              <p className="text-sm leading-relaxed mb-4 max-w-md">
                {FOUNDATION.mission}
              </p>
              <p className="text-xs text-white/30">
                {FOUNDATION.programme.trademark} is a registered trademark, delivered by {FOUNDATION.name}.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">The Foundation</h4>
              <ul className="space-y-2.5 text-sm">
                <li><FootLink to="/about">About Us</FootLink></li>
                <li><FootLink to="/what-we-do">What We Do</FootLink></li>
                <li><FootLink to="/get-involved">Get Involved</FootLink></li>
                <li><FootLink to="/apply">Apply for Support</FootLink></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Programmes</h4>
              <ul className="space-y-2.5 text-sm">
                <li><FootLink to="/programmes">LifeSpark360</FootLink></li>
                <li>
                  <a href={COURSES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                    Courses <ExternalIcon />
                  </a>
                </li>
                <li><a href={ASSESSMENT_URL} className="hover:text-white transition-colors">Readiness Assessment</a></li>
                <li><FootLink to="/cv-lite">Free CV Check</FootLink></li>
                <li><FootLink to="/policies">Our Policies</FootLink></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 space-y-3">
            <p className="text-xs text-white/40 leading-relaxed max-w-3xl">
              {FOUNDATION.name} is a {FOUNDATION.structure} registered in England and Wales
              (Charity No. {FOUNDATION.charityNumber}), established {FOUNDATION.registeredDate}.
              We operate throughout {FOUNDATION.jurisdictions}. Formerly known as {FOUNDATION.formerName}.
            </p>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <p className="text-xs text-white/30">
                &copy; {new Date().getFullYear()} {FOUNDATION.name}. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-xs text-white/30">
                <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
                <a href={`mailto:${FOUNDATION.email}`} className="hover:text-white/60 transition-colors">{FOUNDATION.email}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      data-active={active}
      className={`nav-underline px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active ? 'text-white' : 'text-white/80 hover:text-white'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Link to={to} onClick={onClick} className="block px-3 py-2.5 text-white/90 hover:text-white text-sm font-medium rounded-md hover:bg-white/5 transition-colors">
      {children}
    </Link>
  );
}

function FootLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="hover:text-white transition-colors">
      {children}
    </Link>
  );
}
